// A multi-day job on the engineer's phone (Session BDM). Pure: no clock, no DOM.
//
// ONE JOB, ONE REFERENCE. The server lists the job once, with the days the engineer is ON (`workDays`). The screens that lay work out
// by day (My jobs, Home, the diary) want it on EVERY one of those days, so this expands the one job into one entry per day, each
// carrying that day's start in `scheduledFor`, a label ("Day 2 of 10") and a `viewKey` that is unique per day (React keys, never the
// job id twice). Opening any of them opens the same job. A job with no `workDays` comes back exactly as it was.
//
// A day the engineer has finished (both an arrival and a departure recorded) reads as complete in the day lists, so it stops showing
// as open work or overdue; the job itself stays open until its one sheet is submitted on the last day.
export interface EngineerWorkDay {
  day: string; // YYYY-MM-DD
  workDayId: string;
  number: number;
  total: number;
  label: string;
  mine: boolean;
  /** overtimeBooked is the ASK; overtimeApproved says the office has said yes (Session OTA). Absent from a cache older than OTA: read as not approved. */
  time: { arrivedAt: string | null; leftAt: string | null; note: string | null; overtimeBooked: boolean; overtimeApproved?: boolean } | null;
}

export interface WorkDayJobLike {
  id: string;
  status: string;
  scheduledFor: string | null;
  workDays?: EngineerWorkDay[];
}

export type DayEntry<T> = T & { viewKey: string; dayLabel: string | null; workDay: EngineerWorkDay | null };

/** The local start of a working day: the job's own time of day (if it has one) on that local date. */
export function workDayStartIso(day: string, scheduledFor: string | null): string {
  const [y, m, d] = day.split("-").map(Number);
  let hh = 9;
  let mm = 0;
  if (scheduledFor) {
    const t = new Date(scheduledFor);
    if (!Number.isNaN(t.getTime())) {
      hh = t.getHours();
      mm = t.getMinutes();
    }
  }
  return new Date(y!, m! - 1, d!, hh, mm).toISOString();
}

export function expandWorkDays<T extends WorkDayJobLike>(jobs: readonly T[]): DayEntry<T>[] {
  const out: DayEntry<T>[] = [];
  for (const job of jobs) {
    const days = job.workDays;
    if (!days || days.length === 0) {
      out.push({ ...job, viewKey: job.id, dayLabel: null, workDay: null });
      continue;
    }
    for (const wd of days) {
      const finished = wd.time?.arrivedAt != null && wd.time?.leftAt != null;
      out.push({
        ...job,
        // A finished day is done with; the job as a whole is not.
        status: finished && (job.status === "scheduled" || job.status === "in_progress") ? "complete" : job.status,
        scheduledFor: workDayStartIso(wd.day, job.scheduledFor),
        viewKey: `${job.id}:${wd.day}`,
        dayLabel: wd.label,
        workDay: wd,
      });
    }
  }
  return out;
}

/** The day the job sheet screen should open on: today if the engineer is on it, else the latest past day not yet finished, else null. */
export function currentWorkDay(job: WorkDayJobLike, todayYmd: string): EngineerWorkDay | null {
  const days = job.workDays ?? [];
  const today = days.find((d) => d.day === todayYmd);
  if (today) return today;
  const unfinished = days.filter((d) => d.day < todayYmd && !(d.time?.arrivedAt && d.time?.leftAt));
  return unfinished.at(-1) ?? null;
}

/** Unique per entry on a list: the job id, plus the day for one day of a multi-day job. */
export const viewKeyOf = (j: { id: string }): string => (j as { viewKey?: string }).viewKey ?? j.id;
/** "Day 2 of 10" for an entry that is one day of a multi-day job, else null. */
export const dayLabelOf = (j: object): string | null => (j as { dayLabel?: string | null }).dayLabel ?? null;
/** Where an entry opens: the one job, on that day's panel for a multi-day job. */
export const jobLinkOf = (j: { id: string }): string => {
  const wd = (j as { workDay?: { day: string } | null }).workDay;
  return `/engineer/jobs/${j.id}${wd ? `?day=${encodeURIComponent(wd.day)}` : ""}`;
};
